import { apiClient } from '@/shared/lib/axios';
import type {
  LoginRequest,
  LoginResponse,
  User,
  VerifyTwoFactorRequest,
  VerifyTwoFactorResponse,
  ResendTwoFactorResponse,
} from '../types/auth.types';

const AUTH_API_PATH = '/api/auth';

interface CsrfTokenResponse {
  csrfToken: string;
}

interface SessionResponse {
  user: User;
  message: string;
}

export const authService = {
  /**
   * CSRFトークンを取得
   */
  getCsrfToken: async (): Promise<string> => {
    const response = await apiClient.get<CsrfTokenResponse>(
      `${AUTH_API_PATH}/csrf-token`
    );
    return response.data.csrfToken;
  },

  /**
   * ユーザーログイン
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    // まずCSRFトークンを取得
    const csrfToken = await authService.getCsrfToken();

    const response = await apiClient.post<LoginResponse>(
      `${AUTH_API_PATH}/login`,
      credentials,
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );
    return response.data;
  },

  /**
   * ユーザーログアウト
   */
  logout: async (): Promise<void> => {
    // CSRFトークンを取得してログアウト
    const csrfToken = await authService.getCsrfToken();
    await apiClient.post(
      `${AUTH_API_PATH}/logout`,
      {},
      {
        headers: {
          'x-csrf-token': csrfToken,
        },
      }
    );
  },

  /**
   * 現在のセッション情報を取得
   */
  getSession: async (): Promise<User> => {
    const response = await apiClient.get<SessionResponse>(
      `${AUTH_API_PATH}/session`
    );
    return response.data.user;
  },

  /**
   * トークンの検証（セッションチェック）
   */
  verifyToken: async (): Promise<boolean> => {
    try {
      await authService.getSession();
      return true;
    } catch {
      return false;
    }
  },

  /**
   * 2FAコード検証
   * pendingTokenはHttpOnly Cookieから自動送信される
   */
  verifyTwoFactor: async (params: VerifyTwoFactorRequest): Promise<VerifyTwoFactorResponse> => {
    const csrfToken = await authService.getCsrfToken();

    const response = await apiClient.post<VerifyTwoFactorResponse>(
      `${AUTH_API_PATH}/verify-2fa`,
      { code: params.code },
      {
        headers: { 'x-csrf-token': csrfToken },
        withCredentials: true,
      }
    );

    return response.data;
  },

  /**
   * 2FAコード再送信
   * pendingTokenはHttpOnly Cookieから自動送信される
   */
  resendTwoFactorCode: async (): Promise<ResendTwoFactorResponse> => {
    const csrfToken = await authService.getCsrfToken();

    const response = await apiClient.post<ResendTwoFactorResponse>(
      `${AUTH_API_PATH}/resend-2fa`,
      {},
      {
        headers: { 'x-csrf-token': csrfToken },
        withCredentials: true,
      }
    );

    return response.data;
  },
};